import dayjs, { type Dayjs } from 'dayjs';

export const applyHhMmToDay = (baseDay: Dayjs, hhMm: string): Dayjs | null => {
  const timePart = dayjs(hhMm, 'HH:mm', true);
  if (!timePart.isValid()) return null;
  return baseDay.hour(timePart.hour()).minute(timePart.minute()).second(0).millisecond(0);
};

export const toDecimalHours = (timeObject: Dayjs) => {
  const hours = timeObject.hour();
  const minutes = timeObject.minute();
  return hours + minutes / 60;
};

export const generateTimeOptions = (): string[] => {
  const times: string[] = [];
  for (let i = 0; i < 24; i++) {
    const hour = i.toString().padStart(2, '0');
    times.push(`${hour}:00`);
    times.push(`${hour}:30`);
  }
  return times;
};

export const getInUTC = (time: Dayjs) => dayjs(time).utc().format('DD-MMMM-YYYY HH:mm:ss');
export const getInUTCTime = (time: Dayjs) => dayjs(time).utc().format('HH:mm:ss');
export const minutesOfDayFromDateTime = (value: Dayjs | string): number => {
  const d = typeof value === 'string' ? dayjs(value) : value;
  return d.hour() * 60 + d.minute();
};

const clockLabelToMinutes = (hhMm: string): number => {
  const parsed = dayjs(hhMm, 'HH:mm', true);
  if (!parsed.isValid()) return NaN;
  return parsed.hour() * 60 + parsed.minute();
};

export const getEndTimeOptionsAfterStart = (
  startTime: Dayjs | string | null | undefined,
  allOptions: string[] = generateTimeOptions(),
): string[] => {
  if (startTime == null) {
    return allOptions;
  }
  const startMinutes = minutesOfDayFromDateTime(startTime);
  const afterStart = allOptions.filter((option) => clockLabelToMinutes(option) > startMinutes);
  const beforeStart = allOptions.filter((option) => clockLabelToMinutes(option) < startMinutes);
  return [...afterStart, ...beforeStart];
};

export const isSameDayTimeRangeInverted = (
  startTime: Dayjs | string | null | undefined,
  endTime: Dayjs | string | null | undefined,
): boolean => {
  if (!startTime || !endTime) return false;
  const s = typeof startTime === 'string' ? dayjs(startTime) : startTime;
  const e = typeof endTime === 'string' ? dayjs(endTime) : endTime;
  // If end is after start as a datetime, it is valid (e.g. overnight schedule on next day)
  if (e.isAfter(s)) return false;
  return minutesOfDayFromDateTime(endTime) <= minutesOfDayFromDateTime(startTime);
};

export const isOvernightShift = (
  startTime: Dayjs | string | null | undefined,
  endTime: Dayjs | string | null | undefined,
): boolean => {
  if (!startTime || !endTime) return false;

  if (typeof startTime === 'string' && typeof endTime === 'string') {
    if (startTime.includes('T') && endTime.includes('T')) {
      return dayjs(endTime).isAfter(dayjs(startTime), 'day');
    }
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    if (!isNaN(sh) && !isNaN(sm) && !isNaN(eh) && !isNaN(em)) {
      return eh * 60 + em < sh * 60 + sm;
    }
  }

  const s = typeof startTime === 'string' ? dayjs(startTime) : startTime;
  const e = typeof endTime === 'string' ? dayjs(endTime) : endTime;
  if (e.isAfter(s, 'day')) return true;

  return minutesOfDayFromDateTime(e) < minutesOfDayFromDateTime(s);
};
